import { useState, type FormEvent } from 'react';
import { ApiError, api } from '../lib/api';
import { useUi } from '@allrouted/ui/Toaster';
import { TimezoneSelect, browserTimezone } from './TimezoneSelect';

/**
 * A new account's first screen.
 *
 * Everything in the console hangs off a territory -- depots, vehicles, orders,
 * the day's plan -- and an account starts with none, so the board would be an
 * empty map with nothing to click. This asks for the one thing that unblocks
 * the rest, then points at what comes next.
 */
export function FirstRun({
  accountName,
  onCreated,
}: {
  accountName: string;
  onCreated: (territoryId: string) => void;
}) {
  const ui = useUi();
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState(browserTimezone);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !name.trim()) return;
    setBusy(true);
    try {
      const created = (await api.createTerritory({
        name: name.trim(),
        timezone,
        planningHorizonDays: 7,
      })) as { id?: string };
      if (!created.id) throw new Error('The territory was not created.');
      ui.notify('ok', `${name.trim()} is ready`, 'Next, add a depot: where your vehicles start and end the day.');
      onCreated(created.id);
    } catch (err) {
      ui.notify('error', 'Could not create the territory', err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className="first-run">
      <div className="first-run-card">
        <span className="eyebrow">Welcome to AllRouted</span>
        <h1>Set up {accountName ? `${accountName}’s` : 'your'} first territory</h1>
        <p className="first-run-lead">
          A territory is an area you dispatch in, with its own depots, vehicles, drivers and timezone. Most companies
          start with one; you can add more any time.
        </p>

        <form className="first-run-form" onSubmit={(e) => void submit(e)}>
          <label className="stack">
            <span className="label">Territory name</span>
            <input
              className="field"
              value={name}
              placeholder="e.g. Columbus, North Region, Main"
              onChange={(e) => setName(e.target.value)}
              autoFocus
              maxLength={120}
            />
          </label>
          <div className="stack">
            <span className="label">Timezone</span>
            <TimezoneSelect value={timezone} onChange={setTimezone} />
          </div>
          <button type="submit" className="btn btn-primary first-run-submit" disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create territory'}
          </button>
        </form>

        <ol className="first-run-steps">
          <li data-state="now">
            <strong>Create a territory</strong>
            <span>Name it and set its timezone.</span>
          </li>
          <li>
            <strong>Add depots</strong>
            <span>Where vehicles start and end the day: a yard, an office or a technician’s home.</span>
          </li>
          <li>
            <strong>Add vehicles and drivers</strong>
            <span>Shifts, capacities and skills.</span>
          </li>
          <li>
            <strong>Add orders, then Build Routes</strong>
            <span>Type them in, import them, or send them through the API.</span>
          </li>
        </ol>
      </div>
    </div>
  );
}
